import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { OptimizedImage } from './OptimizedImage';

// Create properly typed motion components
const MotionDiv = motion.div;

interface ServiceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  details?: string[];
  imageSrc?: string;
  className?: string;
  index?: number;
}

export function ServiceCard({
  icon,
  title,
  description,
  details = [],
  imageSrc,
  className,
  index = 0,
}: ServiceCardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  
  const handleLearnMoreClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const currentPath = location.pathname;
    
    const scrollToServiceDetail = (serviceElement: HTMLElement) => {
      const navbarHeight = 96; // Account for main navbar height
      const stickyNavHeight = 60; // Account for sticky nav height
      const totalOffset = navbarHeight + stickyNavHeight;
      const elementPosition = serviceElement.getBoundingClientRect().top + window.pageYOffset;
      
      window.scrollTo({
        top: elementPosition - totalOffset,
        behavior: 'smooth'
      });
      
      // Add highlight effect
      serviceElement.classList.add('highlight-section');
      setTimeout(() => {
        serviceElement.classList.remove('highlight-section');
      }, 2000);
    };
    
    if (currentPath === '/services') {
      // If already on services page, just scroll to the section
      const serviceElement = document.getElementById(`service-detail-${index}`);
      if (serviceElement) {
        scrollToServiceDetail(serviceElement);
      }
    } else {
      // If on another page (like home), navigate to services page with hash
      navigate(`/services#service-detail-${index}`);
      
      // Wait for navigation to complete, then scroll
      setTimeout(() => {
        const serviceElement = document.getElementById(`service-detail-${index}`);
        if (serviceElement) {
          scrollToServiceDetail(serviceElement);
        }
      }, 800);
    }
  };

  return (
    <MotionDiv
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ 
        y: -12,
        scale: 1.03,
      }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className={cn(
        "group relative bg-white dark:bg-slate-800 rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-700 cursor-pointer h-[480px] border border-gray-100 dark:border-slate-700/50",
        className
      )}
    >
      {/* Image Section - Full card */}
      <div className="relative h-full overflow-hidden">
        {imageSrc ? (
          <OptimizedImage
            src={imageSrc}
            alt={title}
            className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
            containerClassName="h-full"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-ocean/20 via-accent/15 to-ocean-light/20 flex items-center justify-center">
            <div className="text-ocean dark:text-ocean-light opacity-40 scale-150">
              {icon}
            </div>
          </div>
        )}
        
        {/* BEFORE HOVER: Dark overlay with title at bottom */}
        <div className="absolute inset-0 bg-black/60 flex items-end p-6 opacity-100 group-hover:opacity-0 transition-all duration-500 ease-in-out">
          <h3 className="text-xl md:text-2xl font-bold text-white leading-tight">
            {title}
          </h3>
        </div>

        {/* AFTER HOVER: Full dark overlay with content */}
        <div className="absolute inset-0 bg-black/90 opacity-0 group-hover:opacity-100 transition-all duration-500 p-6 flex flex-col">
          {/* Title at top on hover */}
          <div className="pb-2">
            <h3 className="text-xl md:text-2xl font-bold text-white mb-4 transform translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 delay-100">
              {title}
            </h3>
          </div>

          {/* Content area - flex grow to fill space */}
          <div className="flex-1 flex flex-col justify-between">
            {/* Description and details */}
            <div className="transform translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 delay-200">
              <p className="text-white/90 text-base leading-relaxed mb-6">
                {description}
              </p>

              {/* Details list */}
              {details.length > 0 && (
                <div className="mb-6">
                  <div className="space-y-2">
                    {details.slice(0, 2).map((detail, detailIndex) => (
                      <div
                        key={detailIndex}
                        className="flex items-start gap-3 text-white/80 text-sm transform translate-x-4 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-300"
                        style={{ transitionDelay: `${300 + (detailIndex * 100)}ms` }}
                      >
                        <div className="w-1.5 h-1.5 bg-ocean rounded-full mt-2 flex-shrink-0" />
                        <span className="leading-relaxed">{detail}</span>
                      </div>
                    ))}
                    {details.length > 2 && (
                      <div className="text-white/60 text-sm font-medium pl-5 transform translate-x-4 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-300 delay-500">
                        +{details.length - 2} more features
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Learn More button at bottom */}
            <div className="mt-auto transform translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 delay-400">
              <button
                onClick={handleLearnMoreClick}
                className="flex items-center justify-center gap-2 bg-ocean hover:bg-ocean-dark text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 shadow-lg w-full hover:scale-102 hover:shadow-xl"
              >
                <span>Learn More</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

    </MotionDiv>
  );
}


